import React, { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import {
  useGetOrderByIdQuery,
  useGetOrderReceiptDownloadByIdQuery,
} from "src/services/OrderService";

type DetailsProps = {
  title: string;
  children: React.ReactNode;
  isOpen: boolean;
  toggleOpen: () => void;
};

const Details: React.FC<DetailsProps> = ({
  title,
  children,
  isOpen,
  toggleOpen,
}) => (
  <div className="bg-white shadow-md rounded-lg flex flex-col border border-gray-200">
    <div
      className={`px-4 py-3 flex justify-between items-center cursor-pointer transition-colors
        ${isOpen ? "bg-primary-main text-white" : "bg-gray-100 text-gray-800"}`}
      onClick={toggleOpen}
    >
      <span className="font-semibold">{title}</span>
      {isOpen ? <FaChevronUp /> : <FaChevronDown />}
    </div>
    <div
      style={{
        height: isOpen ? "auto" : 0,
        overflow: "hidden",
        transition: "height 0.3s ease",
      }}
    >
      <div className="p-4 space-y-2 text-gray-800 min-h-[50px]">{children}</div>
    </div>
  </div>
);


const formatStatus = (status: string) => {
  if (!status) return "N/A";
  return status.replace(/_/g, " "); 
};

const OrderView: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const [receipt, setReceipt] = useState<any>();
  const { data, isLoading, isError } = useGetOrderByIdQuery(orderId);
  const { data: receiptData, isLoading: isReceiptLoading } =
    useGetOrderReceiptDownloadByIdQuery(orderId);
  useEffect(() => {
    if (isReceiptLoading) {
      return;
    }
    setReceipt(receiptData.data);
  });
  const order = data?.data;

  const [openSections, setOpenSections] = useState({
    order: true,
    payment: true,
    shipping: true,
  });

  // 📄 Ref to receipt section
  const receiptRef = useRef<HTMLDivElement>(null);

  const toggleSection = (section: "order" | "payment" | "shipping") => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // 📥 Print & Download receipt
  const handleDownloadReceipt = () => {
    const pdfUrl = receipt;
    window.open(pdfUrl, "_blank");
  };

  if (isLoading) return <div className="p-6">Loading order details...</div>;
  if (isError || !order)
    return (
      <div className="p-6 text-red-500">Failed to load order details.</div>
    );

  return (
    <div className="space-y-6 p-6">
      <div key={order._id} className="bg-gray-50 p-6 rounded-lg">
        {/* Product Info */}
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-shrink-0 w-32 h-32 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
            <img
              src={order.productImage?.[0]}
              alt={order.productName}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Product name + Download button aligned */}
          <div className="flex-1 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-xl text-gray-800">
                {order.productName}
              </h2>
              <button
                onClick={handleDownloadReceipt}
                className="px-4 py-2 bg-primary-main text-white rounded-lg hover:bg-primary-dark transition"
              >
                Download Receipt
              </button>
            </div>

            <div className="text-gray-600 space-y-1 mt-2">
              <p>
                Qty:{" "}
                <span className="font-medium">{order.productQuantity}</span>
              </p>
              <p>
                Mode: <span className="font-medium">{order.modeName}</span>
              </p>
              <p>
                By:{" "}
                <span className="font-medium">
                  {order.faculty[0]?.facultyName}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Collapsible Sections */}
        <div className="grid md:grid-cols-3 gap-4 mt-4">
          {/* Order Details */}
          <Details
            title="Order Details"
            isOpen={openSections.order}
            toggleOpen={() => toggleSection("order")}
          >
            <p>
              Order ID: <span className="font-medium">{order.orderUnqId}</span>
            </p>
            <p>
              Order Total:{" "}
              <span className="font-medium text-primary-main">
                ₹{order.orderAmt}
              </span>
            </p>
            <p>
              Quantity:{" "}
              <span className="font-medium">{order.productQuantity}</span>
            </p>
            <p>
              Transaction ID:{" "}
              <span className="font-medium">{order.transactionId || "-"}</span>
            </p>
            <p>
              Order Date:{" "}
              <span className="font-medium">
                {new Date(order.createdAt).toLocaleDateString("en-IN")}
              </span>
            </p>
            <p>
              Status:{" "}
              <span
                className={`px-2 py-1 rounded text-xs font-semibold ${
                  order.orderStatus === "CONFIRMED"
                    ? "bg-green-100 text-green-700 border border-green-300"
                    : order.orderStatus === "PENDING"
                    ? "bg-yellow-100 text-yellow-700 border border-yellow-300"
                    : "bg-red-100 text-red-700 border border-red-300"
                }`}
              >
                {formatStatus(order.orderStatus)}
              </span>
            </p>
          </Details>

          {/* Payment Details */}
          <Details
            title="Payment Details"
            isOpen={openSections.payment}
            toggleOpen={() => toggleSection("payment")}
          >
            <p>
              MRP:{" "}
              <span className="font-medium text-primary-main">
                ₹{order.productMrp}
              </span>
            </p>
            <p>
              Discount:{" "}
              <span className="font-medium text-red-600">
                -₹{order.productDiscountAmount}
              </span>
            </p>
            <p>
              Wallet Discount:{" "}
              <span className="font-medium text-red-600">
                -₹{order.walletDiscountAmount}
              </span>
            </p>
            <p>Coupon Code: {order.couponCode || "-"}</p>
            <p>
              Coupon Discount:{" "}
              <span className="font-medium text-red-600">
                ₹{order.couponDiscountAmount}
              </span>
            </p>
            <p className="font-semibold text-gray-800 border-t pt-2">
              Final Price:{" "}
              <span className="font-bold text-primary-main">
                ₹{order.orderAmt}
              </span>
            </p>
          </Details>

          {/* Shipping Details */}
          <Details
            title="Shipping Details"
            isOpen={openSections.shipping}
            toggleOpen={() => toggleSection("shipping")}
          >
            <p>
              Name:{" "}
              <span className="font-medium">
                {order.deliveryDetails.firstName}{" "}
                {order.deliveryDetails.lastName}
              </span>
            </p>
            <p>
              Mobile:{" "}
              <span className="font-medium">
                {order.deliveryDetails.mobile || "-"}
              </span>
            </p>
            <p>
              Email:{" "}
              <span className="font-medium">{order.deliveryDetails.email}</span>
            </p>
            <p>
              Address:{" "}
              <span className="font-medium">
                {order.deliveryDetails.address}
              </span>
            </p>
            <p>
              City:{" "}
              <span className="font-medium">{order.deliveryDetails.city}</span>
            </p>
            <p>
              Pin Code:{" "}
              <span className="font-medium">
                {order.deliveryDetails.pincode}
              </span>
            </p>
            <p>
              State:{" "}
              <span className="font-medium">{order.deliveryDetails.state}</span>
            </p>
            <p>
              Country:{" "}
              <span className="font-medium">
                {order.deliveryDetails.country}
              </span>
            </p>
          </Details>
        </div>
      </div>

      {/* Hidden receipt content (for printing only) */}
      <div ref={receiptRef} className="hidden print:block bg-white p-6">
        <h1 className="text-xl font-bold mb-4">Receipt</h1>
        <p>Order ID: {order.orderUnqId}</p>
        <p>Product: {order.productName}</p>
        <p>Quantity: {order.productQuantity}</p>
        <p>Total: ₹{order.orderAmt}</p>
        <p>Date: {new Date(order.createdAt).toLocaleDateString("en-IN")}</p>
        <hr className="my-4" />
        <h2 className="font-semibold">Shipping Details</h2>
        <p>
          {order.deliveryDetails.firstName} {order.deliveryDetails.lastName}
        </p>
        <p>{order.deliveryDetails.address}</p>
        <p>
          {order.deliveryDetails.city}, {order.deliveryDetails.state},{" "}
          {order.deliveryDetails.country} - {order.deliveryDetails.pincode}
        </p>
        <p>Mobile: {order.deliveryDetails.mobile}</p>
        <p>Email: {order.deliveryDetails.email}</p>
      </div>
    </div>
  );
};

export default OrderView;
